<script lang="ts">
	import { page } from '$app/state';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import IconLabel from '$lib/components/atoms/iconlabel/IconLabel.svelte';
	import Link from '$lib/components/atoms/link/Link.svelte';
	import * as m from '$paraglide/messages';

	const MAX_PAGES = 600;
	interface Props {
		currentPage?: number;
		pageCount: number;
		pageHref?: (page: number) => string;
	}

	const { origin = '', pathname = '' } = $derived(page?.url || {});

	const { currentPage = 1, pageCount, pageHref = n =>
		n !== 1 ? `?page=${n}` : origin + pathname }: Props = $props();

	let isExpanded = $state(false);

	/**
	 * calculate page numbers to show.
	 * first page, last page
	 * all pages in the same 10 range,
	 * all multiples of 10 in the same 100 range
	 * all multiples of 100 below pageCount or MAX_PAGES
	 * @param currentPage
	 * @param pageCount
	 */
	function calculate(currentPage: number, pageCount: number) {
		const lower10 = currentPage - (currentPage % 10);
		const lower100 = currentPage - (currentPage % 100);
		const max = Math.min(pageCount, MAX_PAGES);
		const max100 = max - (max % 100);
		return [
			...new Set(
				[
					1,
					...Array.from({ length: 9 }, (_, i) => lower10 + i + 1), // 121,122,...
					...Array.from({ length: 9 }, (_, i) => lower100 + 10 * (i + 1)), // 110,120,...
					...Array.from({ length: max100 / 100 }, (_, i) => (i + 1) * 100), // 100,200 ...
					pageCount,
				].filter(p => p > 0 && p <= max),
			),
		].sort((a, b) => a - b);
	}

	const pages = $derived(calculate(currentPage, pageCount));
</script>

<div>
	<h2 class="border-b-1 border-b-solid text-coloredneutral-soft text-bold text-md m-0">
		<Button
			variant="ghost"
			iconPosition="right"
			onclick={() => (isExpanded = !isExpanded)}
			class="w-full justify-between"
		>
			<IconLabel
				icon="chevron-right"
				label={m.pagination__aria_labels__nav()}
				iconPosition="right"
				iconClasses={isExpanded ? 'rotate-90' : undefined}
				labelClasses="text-left"
				tag="span"
				iconTag="span"
			/>
		</Button>
	</h2>
	<div class="pagination" class:show={isExpanded}>
		<nav aria-label={m.pagination__aria_labels__nav()}>
			<!-- eslint-disable-next-line svelte/require-each-key -->
			{#each pages as page, index (index)}
				<Link
					href={pageHref(page)}
					active={page === currentPage}
					ariaLabel={page === currentPage
						? m.pagination__aria_labels__page_active({ page })
						: m.pagination__aria_labels__page({ page })}
					class="inline px-1 text-nowrap">
					{page.toString()}
				</Link>
			{/each}
		</nav>
	</div>
</div>

<style>
	.pagination {
		height: 0;
		overflow: hidden;
		margin: 0;
		padding: 0;
	}
	.pagination.show {
		height: auto;
		padding: 0.5rem;
	}
</style>
